(() => {
  const MOBILE_QUERY = "(max-width: 749px)";
  const SWIPE_THRESHOLD = 52;
  const MAX_SCALE = 4;
  let viewer;
  let state;
  const supportsViewer = () => typeof window.PointerEvent === "function" && typeof window.HTMLDialogElement === "function" && typeof HTMLDialogElement.prototype.showModal === "function";
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;
  function imageItem(image, mediaId = "") {
    const src = (image == null ? void 0 : image.currentSrc) || (image == null ? void 0 : image.src) || "";
    if (!src) return null;
    return {
      src,
      srcset: image.getAttribute("srcset") || "",
      alt: image.alt || "",
      mediaId
    };
  }
  function itemKey(item) {
    try {
      const url = new URL(item.src, document.baseURI);
      url.searchParams.delete("width");
      return url.href;
    } catch (_error) {
      return item.src;
    }
  }
  function collectItems(productInfo, group) {
    const mainItems = Array.from(productInfo.querySelectorAll(".product__media-item .product__modal-opener--image")).filter((opener) => !opener.closest(".product__media-item--variant[hidden]")).map((opener) => {
      var _a, _b;
      return imageItem(
        opener.querySelector(".product__media img"),
        ((_a = opener.querySelector("[data-media-id]")) == null ? void 0 : _a.dataset.mediaId) || ((_b = opener.closest("[data-media-id]")) == null ? void 0 : _b.dataset.mediaId) || ""
      );
    });
    const detailItems = Array.from(productInfo.querySelectorAll(".product__description img")).filter((image) => !image.closest("a")).map((image) => imageItem(image));
    const keys = /* @__PURE__ */ new Set();
    const groupItems = group === "detail" ? detailItems : mainItems;
    return groupItems.filter((item) => {
      if (!item) return false;
      const key = itemKey(item);
      if (!key || keys.has(key)) return false;
      keys.add(key);
      return true;
    });
  }
  function resetTransform() {
    if (!state) return;
    state.scale = 1;
    state.x = 0;
    state.y = 0;
    viewer.image.style.setProperty("--buddyora-viewer-scale", "1");
    viewer.image.style.setProperty("--buddyora-viewer-x", "0px");
    viewer.image.style.setProperty("--buddyora-viewer-y", "0px");
    viewer.dialog.classList.remove("is-zoomed");
  }
  function renderTransform() {
    viewer.image.style.setProperty("--buddyora-viewer-scale", String(state.scale));
    viewer.image.style.setProperty("--buddyora-viewer-x", `${state.x}px`);
    viewer.image.style.setProperty("--buddyora-viewer-y", `${state.y}px`);
    viewer.dialog.classList.toggle("is-zoomed", state.scale > 1.01);
  }
  function show(index) {
    state.index = (index + state.items.length) % state.items.length;
    const item = state.items[state.index];
    resetTransform();
    viewer.image.alt = item.alt;
    viewer.image.srcset = item.srcset;
    viewer.image.src = item.src;
    viewer.counter.textContent = `${state.index + 1} / ${state.items.length}`;
  }
  function close() {
    if (viewer && viewer.dialog && viewer.dialog.open) viewer.dialog.close();
  }
  function ensureViewer() {
    if (viewer || !supportsViewer()) return;
    const dialog = document.createElement("dialog");
    dialog.className = "buddyora-mobile-media-viewer";
    dialog.setAttribute("aria-label", "Product image viewer");
    dialog.innerHTML = `
      <div class="buddyora-mobile-media-viewer__toolbar">
        <span data-viewer-counter></span>
        <button type="button" aria-label="Close image viewer" data-viewer-close>&times;</button>
      </div>
      <div class="buddyora-mobile-media-viewer__viewport" data-viewer-viewport>
        <img class="buddyora-mobile-media-viewer__image" alt="" draggable="false" data-viewer-image>
      </div>`;
    document.body.appendChild(dialog);
    viewer = {
      dialog,
      viewport: dialog.querySelector("[data-viewer-viewport]"),
      image: dialog.querySelector("[data-viewer-image]"),
      counter: dialog.querySelector("[data-viewer-counter]"),
      close: dialog.querySelector("[data-viewer-close]")
    };
    dialog.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest("[data-viewer-image], [data-viewer-close]")) return;
      close();
    });
    dialog.addEventListener("close", () => {
      document.body.classList.remove("overflow-hidden");
      const trigger = state && state.trigger;
      state = null;
      if (trigger && typeof trigger.focus === "function") trigger.focus({ preventScroll: true });
    });
    viewer.close.addEventListener("click", close);
    viewer.viewport.addEventListener("pointerdown", (event) => {
      if (!state) return;
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      state.start = { x: event.clientX, y: event.clientY, panX: state.x, panY: state.y, scale: state.scale };
      if (state.pointers.size === 2) {
        const points = [...state.pointers.values()];
        state.pinchDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        state.pinchScale = state.scale;
      }
      if (typeof viewer.viewport.setPointerCapture === "function") {
        viewer.viewport.setPointerCapture(event.pointerId);
      }
    });
    viewer.viewport.addEventListener("pointermove", (event) => {
      if (!(state == null ? void 0 : state.pointers.has(event.pointerId))) return;
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const points = [...state.pointers.values()];
      if (points.length >= 2 && state.pinchDistance) {
        event.preventDefault();
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        state.scale = clamp(state.pinchScale * distance / state.pinchDistance, 1, MAX_SCALE);
        if (state.scale <= 1.01) state.x = state.y = 0;
        renderTransform();
      } else if (state.scale > 1.01 && state.start) {
        event.preventDefault();
        state.x = state.start.panX + event.clientX - state.start.x;
        state.y = state.start.panY + event.clientY - state.start.y;
        renderTransform();
      }
    });
    const endPointer = (event) => {
      if (!(state == null ? void 0 : state.pointers.has(event.pointerId))) return;
      const start = state.start;
      state.pointers.delete(event.pointerId);
      if (!state.pointers.size && state.scale <= 1.01 && start) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5 && state.items.length > 1) {
          show(state.index + (dx < 0 ? 1 : -1));
        }
      }
      if (!state.pointers.size) state.start = null;
    };
    viewer.viewport.addEventListener("pointerup", endPointer);
    viewer.viewport.addEventListener("pointercancel", endPointer);
    viewer.image.addEventListener("dblclick", (event) => {
      event.preventDefault();
      state.scale = state.scale > 1.01 ? 1 : 2;
      if (state.scale === 1) state.x = state.y = 0;
      renderTransform();
    });
  }
  function open(productInfo, trigger, triggerItem, group) {
    if (!supportsViewer()) return;
    const items = collectItems(productInfo, group);
    const key = itemKey(triggerItem);
    const index = items.findIndex((item) => triggerItem.mediaId && item.mediaId === triggerItem.mediaId || itemKey(item) === key);
    if (index < 0 || !items.length) return;
    ensureViewer();
    state = { items, index, trigger, scale: 1, x: 0, y: 0, pointers: /* @__PURE__ */ new Map(), start: null };
    show(index);
    document.body.classList.add("overflow-hidden");
    if (!viewer || typeof viewer.dialog.showModal !== "function") return;
    viewer.dialog.showModal();
    viewer.close.focus({ preventScroll: true });
  }
  document.addEventListener("click", (event) => {
    var _a, _b;
    if (!supportsViewer() || !isMobile() || !(event.target instanceof Element)) return;
    const productInfo = event.target.closest("product-info");
    if (!productInfo || productInfo.dataset.imageZoom !== "lightbox") return;
    const opener = event.target.closest(".product__modal-opener--image");
    const detailImage = event.target.closest(".product__description img");
    if (!opener && (!detailImage || detailImage.closest("a"))) return;
    const image = detailImage || opener.querySelector(".product__media img");
    const mediaId = ((_a = opener == null ? void 0 : opener.querySelector("[data-media-id]")) == null ? void 0 : _a.dataset.mediaId) || ((_b = opener == null ? void 0 : opener.closest("[data-media-id]")) == null ? void 0 : _b.dataset.mediaId) || "";
    const item = imageItem(image, mediaId);
    if (!item) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(productInfo, opener || detailImage, item, opener ? "main" : "detail");
  }, true);
})();
